type Tone = '1000' | '900' | '800' | '700' | '600' | '500' | '400' | '300' | '200' | '100' | '50' | '20' | '000';

const TONES: Tone[] = ['1000', '900', '800', '700', '600', '500', '400', '300', '200', '100', '50', '20', '000'];

interface ShadeSeed {
  tone: Tone;
  hex: string;
}

export function generateShades(input: string | ShadeSeed[]): Record<Tone, string> {
  const seeds: ShadeSeed[] = typeof input === 'string' ? [{ tone: '1000', hex: input }] : input;

  if (!seeds.length) {
    throw new Error('At least one base color must be provided.');
  }

  // Normalize seeds and convert to HSL
  const hslByIndex: ({ h: number; s: number; l: number } | null)[] = new Array(TONES.length).fill(null);

  for (const seed of seeds) {
    const idx = TONES.indexOf(seed.tone);
    if (idx === -1) {
      throw new Error(`Invalid tone: ${seed.tone}`);
    }

    const hex = normalizeHex(seed.hex);
    const rgb = hexToRgb(hex);
    if (!rgb) {
      throw new Error(`Invalid HEX: ${seed.hex}`);
    }

    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    hslByIndex[idx] = hsl;
  }

  const knownIndices = hslByIndex.map((v, i) => (v ? i : -1)).filter(i => i !== -1);

  if (!knownIndices.length) {
    throw new Error('No base color could be converted to HSL.');
  }

  // Interpolation between known colors
  for (let k = 0; k < knownIndices.length - 1; k++) {
    const startIndex = knownIndices[k];
    const endIndex = knownIndices[k + 1];

    const start = hslByIndex[startIndex]!;
    const end = hslByIndex[endIndex]!;

    const steps = endIndex - startIndex;
    for (let step = 1; step < steps; step++) {
      const t = step / steps;
      hslByIndex[startIndex + step] = {
        h: interpolateHue(start.h, end.h, t),
        s: lerp(start.s, end.s, t),
        l: lerp(start.l, end.l, t),
      };
    }
  }

  // Extrapolation towards darker tones than the darkest known
  const firstIdx = knownIndices[0];
  const lastIdx = knownIndices[knownIndices.length - 1];

  const L_MIN = 0.03;
  if (firstIdx > 0) {
    const anchor = hslByIndex[firstIdx]!;
    for (let idx = 0; idx < firstIdx; idx++) {
      const t = idx / firstIdx;
      const l = L_MIN + (anchor.l - L_MIN) * t;
      hslByIndex[idx] = {
        h: anchor.h,
        s: anchor.s,
        l,
      };
    }
  }

  const L_MAX = 0.98;
  if (lastIdx < TONES.length - 1) {
    const anchor = hslByIndex[lastIdx]!;
    const totalSteps = TONES.length - 1 - lastIdx;
    for (let offset = 1; offset <= totalSteps; offset++) {
      const idx = lastIdx + offset;
      const t = offset / totalSteps;
      const l = anchor.l + (L_MAX - anchor.l) * t;
      hslByIndex[idx] = {
        h: anchor.h,
        s: anchor.s,
        l,
      };
    }
  }

  // Convert to HEX
  const result: Partial<Record<Tone, string>> = {};
  hslByIndex.forEach((hsl, idx) => {
    if (!hsl) {
      throw new Error('Internal error: tone not calculated.');
    }
    const { r, g, b } = hslToRgb(hsl.h, hsl.s, hsl.l);
    result[TONES[idx]] = rgbToHex(r, g, b);
  });

  return result as Record<Tone, string>;
}

function normalizeHex(hex: string): string {
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  if (h.length !== 6) {
    throw new Error(`Invalid HEX: ${hex}`);
  }
  return `#${h.toLowerCase()}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length !== 6) return null;

  const num = parseInt(h, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;

  return { r, g, b };
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) => {
    const s = v.toString(16);
    return s.length === 1 ? '0' + s : s;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// r, g, b in [0,255] -> h in [0,360), s,l in [0,1]
function rgbToHsl(r: number, g: number, b: number) {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case rNorm:
        h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 1 * 0);
        break;
      case gNorm:
        h = (bNorm - rNorm) / d + 2;
        break;
      case bNorm:
        h = (rNorm - gNorm) / d + 4;
        break;
    }
    h = h * 60; // to degrees
  }

  return { h, s, l };
}

// h in [0,360), s,l in [0,1] -> r,g,b in [0,255]
function hslToRgb(h: number, s: number, l: number) {
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const Hp = h / 60;
  const X = C * (1 - Math.abs((Hp % 2) - 1));

  let r1 = 0,
    g1 = 0,
    b1 = 0;

  if (0 <= Hp && Hp < 1) {
    r1 = C;
    g1 = X;
    b1 = 0;
  } else if (1 <= Hp && Hp < 2) {
    r1 = X;
    g1 = C;
    b1 = 0;
  } else if (2 <= Hp && Hp < 3) {
    r1 = 0;
    g1 = C;
    b1 = X;
  } else if (3 <= Hp && Hp < 4) {
    r1 = 0;
    g1 = X;
    b1 = C;
  } else if (4 <= Hp && Hp < 5) {
    r1 = X;
    g1 = 0;
    b1 = C;
  } else {
    r1 = C;
    g1 = 0;
    b1 = X;
  }

  const m = l - C / 2;
  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);

  return { r, g, b };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// Hue interpolation that takes the shortest path around the color wheel
function interpolateHue(h1: number, h2: number, t: number): number {
  const delta = ((h2 - h1 + 540) % 360) - 180;
  return (h1 + delta * t + 360) % 360;
}
